import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { portalApi } from '../lib/api';
import { useToast } from '../shared/components/ToastProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';
import { CERT_TYPE_LABELS, GAS_CLASSIFICATION_LABELS } from '../data/mappers';

// Certificate detail/view (plan §1.3): reuse the wizard's PDF pipeline via
// portal-api — no new Forme surface (cert-stabilization gate respected).
export default function CertificateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [cert, setCert] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    pbEngineer.collection('certificates').getOne(id, { expand: 'client_id,engineer_job_id' })
      .then(setCert)
      .catch((err) => {
        console.error('Failed to load certificate:', err);
        notify('Could not load this certificate', 'error');
      })
      .finally(() => setLoading(false));
  }, [id, notify]);

  const handlePdf = async () => {
    if (!cert) return;
    setPreviewing(true);
    try {
      const blob = await portalApi.fetchCertificatePdf(cert.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      notify(err.message || 'Failed to generate PDF', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return <div className="page-container"><Skeleton rows={6} /></div>;

  if (!cert) {
    return (
      <div className="page-container">
        <EmptyState
          title="Certificate not found"
          message="It may have been removed or you don't have access."
          action="Back to Certificates"
          onAction={() => navigate('..', { relative: 'path' })}
        />
      </div>
    );
  }

  const body = (() => {
    try {
      return typeof cert.form_data === 'string' ? JSON.parse(cert.form_data) : (cert.form_data || {});
    } catch {
      return {};
    }
  })();

  const rows = [
    ['Certificate number', cert.certificate_number || '—'],
    ['Type', CERT_TYPE_LABELS[cert.type] || cert.type],
    ['Status', cert.status],
    ['Client', cert.expand?.client_id?.name || cert.client_id || '—'],
    ['Property', body.property_address || cert.expand?.engineer_job_id?.address || '—'],
    ['Landlord', body.landlord_name || '—'],
    ['Engineer', body.engineer_name || cert.engineer_name || '—'],
    ['Gas Safe no.', body.engineer_registration_number || '—'],
    ['Issued', cert.issued_date ? new Date(cert.issued_date).toLocaleDateString() : '—'],
    ['Expires', cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString() : '—'],
    ['Classification', GAS_CLASSIFICATION_LABELS[cert.classification] || cert.classification || '—'],
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{CERT_TYPE_LABELS[cert.type] || cert.type}</h1>
          <p>{cert.certificate_number || 'No number assigned'}</p>
        </div>
        <div className="header-actions">
          <button className="secondary-button" onClick={handlePdf} disabled={previewing}>
            <SafeIcon icon={Icons.FileText} size={15} />
            {previewing ? 'Generating…' : 'PDF Preview'}
          </button>
          <button className="secondary-button" onClick={() => navigate('..', { relative: 'path' })}>
            <SafeIcon icon={Icons.ArrowLeft} size={15} />
            Back
          </button>
        </div>
      </div>

      <div className="parts-detail">
        <div className="detail-panel">
          <h3>Certificate details</h3>
          <table className="spec-table">
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {Array.isArray(body.appliances) && body.appliances.length > 0 && (
          <div className="detail-panel">
            <h3>Appliances ({body.appliances.length})</h3>
            <table className="spec-table">
              <tbody>
                {body.appliances.map((app, i) => (
                  <tr key={i}>
                    <td>{app.type || 'Appliance'}</td>
                    <td>
                      {app.make || ''} {app.model || ''}
                      {app.safe_to_use ? ` · ${String(app.safe_to_use).toUpperCase()}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {Array.isArray(body.faults) && body.faults.length > 0 && (
          <div className="detail-panel">
            <h3>Faults ({body.faults.length})</h3>
            <table className="spec-table">
              <tbody>
                {body.faults.map((fault, i) => (
                  <tr key={i}>
                    <td>{fault.location || 'Fault'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{fault.classification || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cert.notes && (
          <div className="detail-panel">
            <h3>Notes</h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>{cert.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
